import { useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import {
  useAdminRoadmap,
  useCreateRoadmapItem,
  useUpdateRoadmapItem,
  useDeleteRoadmapItem,
  RoadmapItem,
  RoadmapStatus,
} from '@/hooks/useRoadmap';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS: Record<RoadmapStatus, string> = {
  considering: 'Considering',
  planned: 'Planned',
  in_development: 'In Development',
  shipped: 'Shipped',
};

const STATUS_COLORS: Record<RoadmapStatus, string> = {
  considering: 'bg-gray-100 text-gray-700',
  planned: 'bg-blue-100 text-blue-700',
  in_development: 'bg-amber-100 text-amber-700',
  shipped: 'bg-emerald-100 text-emerald-700',
};

const CATEGORIES = ['safety', 'wellness', 'family', 'community', 'general'];

interface FormState {
  title: string;
  description: string;
  category: string;
  status: RoadmapStatus;
  sort_order: number;
  is_visible: boolean;
  icon_name: string;
  shipped_at: string;
}

const EMPTY_FORM: FormState = {
  title: '',
  description: '',
  category: 'general',
  status: 'considering',
  sort_order: 0,
  is_visible: false,
  icon_name: '',
  shipped_at: '',
};

export default function AdminRoadmap() {
  const { user } = useAdminAuth();
  const { data: items, isLoading } = useAdminRoadmap();
  const createMutation = useCreateRoadmapItem();
  const updateMutation = useUpdateRoadmapItem();
  const deleteMutation = useDeleteRoadmapItem();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsFormOpen(true);
  };

  const openEdit = (item: RoadmapItem) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      description: item.description,
      category: item.category,
      status: item.status,
      sort_order: item.sort_order,
      is_visible: item.is_visible,
      icon_name: item.icon_name || '',
      shipped_at: item.shipped_at ? item.shipped_at.split('T')[0] : '',
    });
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      category: form.category,
      status: form.status,
      sort_order: form.sort_order,
      is_visible: form.is_visible,
      icon_name: form.icon_name.trim() || null,
      shipped_at: form.shipped_at ? new Date(form.shipped_at).toISOString() : null,
    };

    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, ...payload });
        toast.success('Item updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Item created');
      }
      setIsFormOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      toast.success('Item deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete');
    }
    setDeleteId(null);
  };

  const toggleVisibility = async (item: RoadmapItem) => {
    try {
      await updateMutation.mutateAsync({ id: item.id, is_visible: !item.is_visible });
      toast.success(item.is_visible ? 'Hidden from public' : 'Now visible to public');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update');
    }
  };

  if (!user) return null;

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Feature Roadmap</h1>
          <p className="text-muted-foreground mt-1">
            Manage items shown on the public "What's Coming" page
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap gap-3 mb-6">
        {(Object.keys(STATUS_LABELS) as RoadmapStatus[]).map((s) => {
          const count = items?.filter((i) => i.status === s).length || 0;
          return (
            <Badge key={s} variant="outline" className={`${STATUS_COLORS[s]} px-3 py-1 text-sm`}>
              {STATUS_LABELS[s]}: {count}
            </Badge>
          );
        })}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !items?.length ? (
        <p className="text-center text-muted-foreground py-12">No roadmap items yet. Add your first one.</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-center">Visible</TableHead>
                <TableHead className="text-center">Order</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium max-w-[280px]">
                    <div>{item.title}</div>
                    {item.description && (
                      <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {item.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_COLORS[item.status]} text-xs`}>
                      {STATUS_LABELS[item.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize text-xs">{item.category}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={item.is_visible}
                      onCheckedChange={() => toggleVisibility(item)}
                    />
                  </TableCell>
                  <TableCell className="text-center text-sm text-muted-foreground">
                    {item.sort_order}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(item.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Roadmap Item' : 'Add Roadmap Item'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Feature name"
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="1-3 sentence summary"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(v: RoadmapStatus) => setForm({ ...form, status: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="considering">Considering</SelectItem>
                    <SelectItem value="planned">Planned</SelectItem>
                    <SelectItem value="in_development">In Development</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label htmlFor="icon_name">Icon Name (lucide)</Label>
                <Input
                  id="icon_name"
                  value={form.icon_name}
                  onChange={(e) => setForm({ ...form, icon_name: e.target.value })}
                  placeholder="e.g. ShieldAlert"
                />
              </div>
            </div>
            {form.status === 'shipped' && (
              <div>
                <Label htmlFor="shipped_at">Shipped Date</Label>
                <Input
                  id="shipped_at"
                  type="date"
                  value={form.shipped_at}
                  onChange={(e) => setForm({ ...form, shipped_at: e.target.value })}
                />
              </div>
            )}
            <div className="flex items-center justify-between pt-2">
              <Label htmlFor="is_visible">Visible to public</Label>
              <Switch
                id="is_visible"
                checked={form.is_visible}
                onCheckedChange={(checked) => setForm({ ...form, is_visible: checked })}
              />
            </div>
            <Button
              onClick={handleSave}
              className="w-full"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {editingId ? 'Save Changes' : 'Create Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete roadmap item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this item from the roadmap. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
